import { useState, useEffect, useCallback } from "react";
import "../../../assets/css/student/blogs/NewStory.css";

/* ══════════════════════════════════════════════════
   DEMO DATA — update fetchStory() for real API
══════════════════════════════════════════════════ */
const DEMO_STORY = {
  id: 1,
  title: "Gardening Business History and Evolution Over Time",
  short_description:
    "A girl who achieved her dream despite the difficulties in her home. Her story is for everyone who has already given up.",
  full_story: `My name is Sana. I grew up in a small home where even electricity would sometimes disappear. But inside me there was a fire — a desire to learn and move forward.

When everyone slept, I studied in the light of my mobile phone. People tried to stop me, saying "what will a girl do after studying?" — but I did not stop.

Today I am the founder of my own company. Not because I was "smart" — but because I never believed that my dream was small.

Your dream is not small either. Just start.`,
  image_path: "https://i.pinimg.com/1200x/dd/d8/04/ddd804b3b20700895071f2088b76b3dd.jpg",
  created_at: new Date().toISOString(),
};

/* ── ICONS ── */
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DoubleArrow = () => (
  <svg width="20" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
    <path d="M0 6H14M14 6L8 1M14 6L8 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 6H20M20 6L14 1M20 6L14 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
  </svg>
);

/* ══════════════════════════════════════════════════
   MODAL
══════════════════════════════════════════════════ */
function StoryModal({ story, onClose }) {
  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-PK", {
      year: "numeric", month: "long", day: "numeric",
    });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="ls-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ls-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="ls-modal">
        <button className="ls-modal-close" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>

        {story.image_path && (
          <img className="ls-modal-image" src={story.image_path} alt={story.title} />
        )}

        <div className={`ls-modal-body${!story.image_path ? " ls-modal-body--no-img" : ""}`}>
          <div className="ls-modal-meta">
            <span className="ls-modal-tag">✦ New Blog Updated</span>
            <span className="ls-modal-date">{formatDate(story.created_at)}</span>
          </div>

          <h2 className="ls-modal-title" id="ls-modal-title">{story.title}</h2>
          <div className="ls-modal-divider" />
          <p className="ls-modal-story">{story.full_story}</p>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════════════════ */
function SectionHeader() {
  return (
    <div className="ls-heading-wrap">
      <p className="ls-eyebrow">
        <span className="ls-eyebrow-line" />
        Latest Story
        <span className="ls-eyebrow-line" />
      </p>
      <h2 className="ls-section-title">
        Every Story Begins With <em>One Brave Step</em>
      </h2>
      <p className="ls-subtitle">A new inspiring story every day — fuel for your dreams</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function LatestStorySection() {
  const [story, setStory]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModal]   = useState(false);
  const [imgError, setImgError] = useState(false);

  const openModal  = useCallback(() => setModal(true),  []);
  const closeModal = useCallback(() => setModal(false), []);

  /* ── DATA FETCH ──
     Uncomment for real backend

     useEffect(() => {
       fetch('/admin-story-upload.php?action=get_story')
         .then(r => r.json())
         .then(d => { setStory(d.success ? d.story : null); setLoading(false); })
         .catch(() => setLoading(false));
     }, []);
  */

  useEffect(() => {
    const t = setTimeout(() => { setStory(DEMO_STORY); setLoading(false); }, 800);
    return () => clearTimeout(t);
  }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-PK", {
      day: "numeric", month: "short", year: "numeric",
    });

  const hasImage = story?.image_path && !imgError;

  /* ── LOADING ── */
  if (loading) return (
    <section className="ls-section">
      <div className="ls-container">
        <SectionHeader />
        <div className="ls-loading-wrap">
          <div className="ls-spinner" />
          <span className="ls-loading-text">Loading story…</span>
        </div>
      </div>
    </section>
  );

  /* ── EMPTY ── */
  if (!story) return (
    <section className="ls-section">
      <div className="ls-container">
        <SectionHeader />
        <div className="ls-empty">
          <div className="ls-empty-icon">📭</div>
          <p className="ls-empty-text">No story available yet — coming soon!</p>
        </div>
      </div>
    </section>
  );

  /* ── MAIN ── */
  return (
    <>
      <section className="ls-section">
        <div className="ls-container">
          <SectionHeader />

          <div className="ls-card">

            {hasImage ? (
              <img
                className="ls-card-image"
                src={story.image_path}
                alt={story.title}
                onError={() => setImgError(true)}
                loading="lazy"
              />
            ) : (
              <div className="ls-card-image" style={{
                background: "linear-gradient(135deg,#3a4a2a,#6a7a4a)",
              }} />
            )}

            <div className="ls-card-overlay" />

            <div className="ls-logo">
              New<br />Story
            </div>

            <div className="ls-card-content">
              <h3 className="ls-card-title">{story.title}</h3>

              <button className="ls-read-btn" onClick={openModal}>
                READ MORE
                <span className="ls-read-btn-arrows">
                  <DoubleArrow />
                </span>
              </button>
            </div>

          </div>

        </div>
      </section>

      {modalOpen && <StoryModal story={story} onClose={closeModal} />}
    </>
  );
}